/**
 * Choose Options Component
 * Handles the custom product option selectors (Style, Absorbency, Colour)
 * - Desktop: Standard button dropdowns
 * - Mobile: Bottom sheet modals
 */

class ChooseOptions extends HTMLElement {
  constructor() {
    super();
    this.isMobile = window.innerWidth < 750;
    this.buttons = this.querySelectorAll('.choose-option__button');
    this.modals = this.querySelectorAll('.choose-option-modal');
    this.dropdowns = this.querySelectorAll('.choose-option__dropdown');
    this.currentOpenDropdown = null;

    // Function to create shortened style names
    this.getShortenedStyleName = (fullName) => {
      if (!fullName) return fullName;
      // Remove " Brief" and " Short" suffixes
      return fullName.replace(' Brief', '').replace(' Short', '');
    };

    this.init();
  }

  init() {
    // Add click handlers to dropdown buttons
    this.buttons.forEach(button => {
      button.addEventListener('click', this.handleButtonClick.bind(this));
    });

    // Add handlers to modals (mobile)
    this.modals.forEach(modal => {
      const backdrop = modal.querySelector('.choose-option-modal__backdrop');
      const closeBtn = modal.querySelector('.choose-option-modal__close');
      const options = modal.querySelectorAll('.choose-option-modal__option');

      // Close on backdrop click
      if (backdrop) {
        backdrop.addEventListener('click', () => this.closeModal(modal));
      }

      // Close on close button click
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.closeModal(modal));
      }

      // Handle option selection
      options.forEach(option => {
        option.addEventListener('click', () => {
          this.handleModalOptionSelect(modal, option);
        });
      });
    });

    // Add handlers to desktop dropdowns
    this.dropdowns.forEach(dropdown => {
      const options = dropdown.querySelectorAll('.choose-option__dropdown-option');

      options.forEach(option => {
        option.addEventListener('click', (e) => {
          e.stopPropagation();
          this.handleDropdownOptionSelect(dropdown, option);
        });
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.closeAllDropdowns();
      }
    });

    // Handle resize
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  handleButtonClick(event) {
    event.stopPropagation();
    const button = event.currentTarget;
    const optionType = button.getAttribute('data-option-type');

    // On mobile, open the modal
    if (this.isMobile) {
      const modal = this.querySelector(`[data-modal="${optionType}"]`);
      if (modal) {
        this.openModal(modal);
      }
    } else {
      // On desktop, toggle the dropdown
      const dropdown = button.nextElementSibling;
      if (dropdown && dropdown.classList.contains('choose-option__dropdown')) {
        this.toggleDropdown(dropdown);
      }
    }
  }

  toggleDropdown(dropdown) {
    const isOpen = dropdown.classList.contains('is-open');

    // Close all other dropdowns first
    this.closeAllDropdowns();

    // Toggle current dropdown
    if (!isOpen) {
      dropdown.classList.add('is-open');
      this.currentOpenDropdown = dropdown;

      // Remove has-selection from all buttons first
      this.buttons.forEach(btn => {
        btn.classList.remove('has-selection');
      });

      // Add active class to the button
      const button = dropdown.previousElementSibling;
      if (button && button.classList.contains('choose-option__button')) {
        button.classList.add('is-active');
      }
    }
  }

  closeAllDropdowns() {
    this.dropdowns.forEach(dropdown => {
      dropdown.classList.remove('is-open');

      // Remove active class from button
      const button = dropdown.previousElementSibling;
      if (button && button.classList.contains('choose-option__button')) {
        button.classList.remove('is-active');
      }
    });
    this.currentOpenDropdown = null;
  }

  handleDropdownOptionSelect(dropdown, option) {
    const value = option.getAttribute('data-value');
    const productUrl = option.getAttribute('data-url');
    const dropdownType = dropdown.getAttribute('data-dropdown');

    // Update selected state
    const allOptions = dropdown.querySelectorAll('.choose-option__dropdown-option');
    allOptions.forEach(opt => {
      opt.classList.remove('choose-option__dropdown-option--selected');
      const radio = opt.querySelector('.choose-option__dropdown-radio');
      if (radio) {
        radio.classList.remove('choose-option__dropdown-radio--checked');
      }
    });

    option.classList.add('choose-option__dropdown-option--selected');
    const selectedRadio = option.querySelector('.choose-option__dropdown-radio');
    if (selectedRadio) {
      selectedRadio.classList.add('choose-option__dropdown-radio--checked');
    }

    // Update button display
    this.updateButtonDisplayFromDropdown(dropdownType, option);

    // Add has-selection class to button to maintain white background
    const button = dropdown.previousElementSibling;
    if (button && button.classList.contains('choose-option__button')) {
      button.classList.add('has-selection');
    }

    // Close dropdown
    this.closeAllDropdowns();

    // Dispatch event with URL
    this.dispatchEvent(new CustomEvent('choose-option-changed', {
      bubbles: true,
      detail: {
        type: dropdownType,
        value: value,
        url: productUrl
      }
    }));

    // Redirect to product URL if available and different from current page
    if (productUrl && productUrl !== '' && productUrl !== window.location.pathname) {
      window.location.href = productUrl;
    }
  }

  updateButtonDisplayFromDropdown(dropdownType, selectedOption) {
    const button = this.querySelector(`[data-option-type="${dropdownType}"]`);
    if (!button) return;

    const value = selectedOption.getAttribute('data-value');

    switch(dropdownType) {
      case 'style':
        const styleText = selectedOption.querySelector('.choose-option__dropdown-text')?.textContent;
        const styleLabel = button.querySelector('.choose-option__label');
        if (styleLabel && styleText) {
          // Use shortened name
          const displayText = this.getShortenedStyleName(styleText);
          styleLabel.textContent = displayText;
        }
        break;

      case 'absorbency':
        const absorbencyIcon = selectedOption.querySelector('.choose-option__dropdown-flow-icon');
        const buttonFlowIcon = button.querySelector('.choose-option__flow-icon');
        if (absorbencyIcon && buttonFlowIcon) {
          buttonFlowIcon.innerHTML = absorbencyIcon.innerHTML;
        }
        break;

      case 'colour':
        const colourText = selectedOption.querySelector('.choose-option__dropdown-text')?.textContent;
        const colourHex = selectedOption.getAttribute('data-swatch');

        const buttonLabel = button.querySelector('.choose-option__label');
        const buttonSwatch = button.querySelector('.choose-option__color-swatch');

        if (buttonLabel && colourText) {
          buttonLabel.textContent = colourText;
        }
        if (buttonSwatch && colourHex) {
          buttonSwatch.style.backgroundColor = colourHex;
        }
        break;
    }
  }

  openModal(modal) {
    modal.classList.add('is-active');
    document.body.classList.add('modal-open');

    // Remove has-selection from all buttons first
    this.buttons.forEach(btn => {
      btn.classList.remove('has-selection');
    });

    // Hide chat widgets on mobile only
    if (this.isMobile) {
      const smileLauncher = document.querySelector('#smile-ui-lite-launcher-frame-container');
      const shopifyChat = document.querySelector('#shopify-chat');

      if (smileLauncher) {
        smileLauncher.style.display = 'none';
      }
      if (shopifyChat) {
        shopifyChat.style.display = 'none';
      }
    }

    // Animate in
    requestAnimationFrame(() => {
      modal.querySelector('.choose-option-modal__content')?.classList.add('is-visible');
    });
  }

  closeModal(modal) {
    modal.querySelector('.choose-option-modal__content')?.classList.remove('is-visible');

    setTimeout(() => {
      modal.classList.remove('is-active');
      document.body.classList.remove('modal-open');

      // Show chat widgets again on mobile AFTER modal fully closes
      if (this.isMobile) {
        const smileLauncher = document.querySelector('#smile-ui-lite-launcher-frame-container');
        const shopifyChat = document.querySelector('#shopify-chat');

        if (smileLauncher) {
          smileLauncher.style.display = '';
        }
        if (shopifyChat) {
          shopifyChat.style.display = '';
        }
      }
    }, 300);
  }

  handleModalOptionSelect(modal, option) {
    const input = option.querySelector('input[type="radio"]');
    const modalType = modal.getAttribute('data-modal');

    if (input) {
      // Update checked state
      input.checked = true;

      // Update the visual state of radio buttons
      const allRadios = modal.querySelectorAll('.choose-option-modal__radio');
      allRadios.forEach(radio => radio.classList.remove('choose-option-modal__radio--checked'));

      const selectedRadio = option.querySelector('.choose-option-modal__radio');
      if (selectedRadio) {
        selectedRadio.classList.add('choose-option-modal__radio--checked');
      }

      // Update the button text/visual based on selection
      this.updateButtonDisplayFromModal(modalType, option);

      // Add has-selection class to button to maintain white background
      const button = this.querySelector(`[data-option-type="${modalType}"]`);
      if (button) {
        button.classList.add('has-selection');
      }

      // Get product URL for navigation
      const productUrl = input.getAttribute('data-url');

      // Dispatch event with URL
      this.dispatchEvent(new CustomEvent('choose-option-changed', {
        bubbles: true,
        detail: {
          type: modalType,
          value: input.value,
          url: productUrl
        }
      }));

      // For colour and absorbency on mobile: redirect immediately without closing modal
      // For style: close modal then redirect (if different page)
      const shouldRedirect = productUrl && productUrl !== '' && productUrl !== window.location.pathname;

      if (shouldRedirect && (modalType === 'colour' || modalType === 'absorbency')) {
        // Keep modal open, redirect immediately
        // The page navigation will naturally "close" the modal
        window.location.href = productUrl;
      } else {
        // Close modal after selection for style or if staying on same page
        this.closeModal(modal);

        // Redirect for style if needed
        if (shouldRedirect) {
          window.location.href = productUrl;
        }
      }
    }
  }

  updateButtonDisplayFromModal(modalType, selectedOption) {
    const button = this.querySelector(`[data-option-type="${modalType}"]`);
    if (!button) return;

    const input = selectedOption.querySelector('input[type="radio"]');
    const value = input?.value;

    switch(modalType) {
      case 'style':
        const styleText = selectedOption.querySelector('.choose-option-modal__text')?.textContent;
        const styleLabel = button.querySelector('.choose-option__label');
        if (styleLabel && styleText) {
          // Use shortened name
          const displayText = this.getShortenedStyleName(styleText);
          styleLabel.textContent = displayText;
        }
        break;

      case 'absorbency':
        const absorbencyModalIcon = selectedOption.querySelector('.choose-option-modal__flow-icon');
        const buttonFlowIcon = button.querySelector('.choose-option__flow-icon');
        if (absorbencyModalIcon && buttonFlowIcon) {
          buttonFlowIcon.innerHTML = absorbencyModalIcon.innerHTML;
        }
        break;

      case 'colour':
        const colourText = selectedOption.querySelector('.choose-option-modal__text')?.textContent;
        const colourHex = input?.getAttribute('data-swatch');

        const buttonLabel = button.querySelector('.choose-option__label');
        const buttonSwatch = button.querySelector('.choose-option__color-swatch');

        if (buttonLabel && colourText) {
          buttonLabel.textContent = colourText;
        }
        if (buttonSwatch && colourHex) {
          buttonSwatch.style.backgroundColor = colourHex;
        }
        break;
    }

    // Dispatch custom event for other components to listen to
    this.dispatchEvent(new CustomEvent('choose-option-changed', {
      bubbles: true,
      detail: { type: modalType, value: value }
    }));
  }

  handleResize() {
    this.isMobile = window.innerWidth < 750;
  }
}

customElements.define('choose-options', ChooseOptions);
